import { useEffect, useMemo, useState } from "react";
import { api, fmtPrice, displayName, priceFor, sizesForType, familyImageFor, padId } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { MoqProgress } from "./MoqProgress";
import { toast } from "sonner";
import { Beaker, Minus, Plus, Sparkles, Search, X } from "lucide-react";

const LEGACY_DUET_TEMPLATE = "Build Your Set · Minimum {qty} Bottles";
const IMPROVED_DUET_TEMPLATE = "Build any combination of {qty} bottles — mix any fragrances or duets.";
const LEGACY_MOQ_HELPER = "MOQ applies to the total number of bottles for this size — you may freely mix any fragrances to complete the required minimum order quantity.";
const IMPROVED_MOQ_HELPER = "Mix any fragrances to complete your minimum order — no need to buy multiples of one scent.";
// Show the improved wording unless an admin has genuinely customised the message.
const pickMsg = (val, legacy, improved) => (val && val !== legacy ? val : improved);

export const ScentAlchemist = () => {
  const { addItem, items } = useCart();
  const [scents, setScents] = useState([]);
  const [catalog, setCatalog] = useState({ concentrations: [], bottle_sizes: [] });
  const [picks, setPicks] = useState([]); // up to 2 scent ids
  const [conc, setConc] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(0);
  const [q, setQ] = useState("");
  const settings = useSettings();
  const msg = settings.messages || {};
  const h = settings.homepage || {};

  useEffect(() => {
    Promise.all([api.get("/scents"), api.get("/catalog")]).then(([s, c]) => {
      setScents(s.data.scents || []);
      setCatalog(c.data);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return scents;
    return scents.filter((s) => {
      const nm = displayName(s).toLowerCase();
      const ib = (s.inspired_by || "").toLowerCase();
      const notes = `${s.top || ""} ${s.heart || ""} ${s.base || ""}`.toLowerCase();
      return nm.includes(term) || ib.includes(term) || notes.includes(term);
    });
  }, [scents, q]);

  // DUET concentrations = all enabled concentrations (EDP, Parfum, Attar)
  const concs = useMemo(() => catalog.concentrations || [], [catalog]);
  const sizes = useMemo(() => (conc ? sizesForType(catalog.bottle_sizes, conc.product_type) : []), [conc, catalog]);

  useEffect(() => { if (concs.length && !conc) setConc(concs[0]); }, [concs, conc]);
  useEffect(() => {
    if (sizes.length) {
      const still = sizes.find((s) => size && s.key === size.key);
      // Default to a standard everyday size — skip the sample size that carries the highest MOQ (order-independent)
      const maxMoq = Math.max(...sizes.map((s) => s.moq || 1));
      const preferred = sizes.find((s) => (s.moq || 1) < maxMoq) || sizes[0];
      const next = still || preferred;
      setSize(next); setQty(0);
    }
  }, [sizes]); // eslint-disable-line

  const toggle = (id) => setPicks((p) => p.includes(id) ? p.filter((x) => x !== id) : (p.length >= 2 ? [p[1], id] : [...p, id]));

  const a = scents.find((s) => s.id === picks[0]);
  const b = scents.find((s) => s.id === picks[1]);
  const ready = a && b && conc && size;
  // Blend price = global variant price (base_price × multiplier)
  const unit = size && conc ? Math.round((size.base_price || 0) * (conc.multiplier || 1)) : 0;
  const moq = size?.moq || 1;
  const total = unit * qty;
  const duetName = ready ? `Duet · ${displayName(a)} + ${displayName(b)}` : "";

  const combinedMoods = useMemo(() => [...new Set([...(a?.moods || []), ...(b?.moods || [])])], [a, b]);
  const combinedOccasions = useMemo(() => [...new Set([...(a?.occasions || []), ...(b?.occasions || [])])], [a, b]);
  // Bottles of the selected size already in the cart (shared MOQ across singles + duets)
  const inCartForSize = useMemo(() => items.filter((i) => size && i.size_key === size.key).reduce((s, i) => s + (i.quantity || 0), 0), [items, size]);

  const add = () => {
    if (!ready) { toast.error("Select two fragrances, an expression and a size."); return; }
    if (qty < 1) { toast.error("Please add at least one bottle."); return; }
    addItem({
      product: "custom", name: duetName, size: size.label, size_key: size.key,
      concentration: conc.key, concentration_label: conc.label, product_type: size.product_type,
      moq, quantity: qty, price: unit, scent_1: a.id, scent_2: b.id,
    });
    toast.success("Added to your collection", { description: `${duetName} · ${size.label} × ${qty}` });
    setPicks([]); setQty(0);
  };

  return (
    <section id="alchemist" className="maroon-panel grain border-y border-white/10 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-12 py-20 sm:py-28">
        <div className="max-w-2xl">
          <p className="eyebrow mb-4 flex items-center gap-2"><Beaker className="w-4 h-4" /> {h.duet_eyebrow || "Scent Alchemist"}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F4EFE6] leading-[1.0]">
            {h.duet_title_line1 || "Create your"} <span className="italic text-[#C5A059]">{h.duet_title_highlight || "signature duet."}</span>
          </h2>
          <p className="mt-6 text-[#F4EFE6]/75 leading-relaxed">
            {h.duet_body || "The two fragrances you select are professionally blended together before bottling to create one unique, singular fragrance — worn only by you."}
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Fragrance picker */}
          <div className="lg:col-span-7">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-5">
              Choose Two Fragrances {picks.length > 0 && `· ${picks.length}/2`}
            </p>
            <div className="relative mb-5">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" strokeWidth={1.6} />
              <input
                data-testid="alchemist-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, inspiration or notes…"
                className="w-full bg-white/5 border border-white/15 pl-11 pr-11 py-3.5 text-sm text-[#F4EFE6] placeholder-white/40 focus:outline-none focus:border-[#C5A059] transition-colors font-mono-label tracking-wide"
              />
              {q && (
                <button data-testid="alchemist-search-clear" onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#C5A059] transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div data-testid="alchemist-scents" className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-[620px] overflow-y-auto custom-scroll pr-1.5">
              {filtered.length === 0 ? (
                <p data-testid="alchemist-no-results" className="col-span-full py-14 text-center text-white/40 font-mono-label text-[11px] uppercase tracking-[0.2em]">No fragrances match “{q}”.</p>
              ) : filtered.map((s) => {
                const sel = picks.includes(s.id);
                const slot = picks.indexOf(s.id);
                const img = familyImageFor(s, catalog.families);
                return (
                  <button key={s.id} data-testid={`alchemist-scent-${s.id}`} onClick={() => toggle(s.id)}
                    className={`relative text-left border overflow-hidden transition-all duration-300 ${sel ? "border-[#C5A059] ring-1 ring-[#C5A059] -translate-y-0.5" : "border-white/10 hover:border-[#C5A059]/50 hover:-translate-y-0.5"}`}>
                    <div className="relative aspect-square bg-[#160309]">
                      <img src={img} alt={displayName(s)} loading="lazy" className={`w-full h-full object-cover transition-opacity duration-300 ${sel ? "opacity-90" : "opacity-70"}`} />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#120306] via-transparent to-transparent" />
                      {sel && <span className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-[#C5A059] text-[#0A0A0A] text-[12px] font-mono-label flex items-center justify-center">{slot + 1}</span>}
                    </div>
                    <div className="p-4">
                      <p className="font-mono-label text-[9px] uppercase tracking-[0.2em] text-[#C5A059]">№ {padId(s.id)}</p>
                      <p className="text-[13px] text-[#F4EFE6] leading-snug mt-1 line-clamp-2">{displayName(s)}</p>
                      {s.family_label && <p className="text-[10px] font-mono-label uppercase tracking-wider text-white/40 mt-1.5">{s.family_label}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Configurator */}
          <div className="lg:col-span-5">
            <div className="lux-card p-8 sticky top-24">
              <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-white/50">Choose Your Fragrances</p>
              <p data-testid="alchemist-name" className="font-serif-display text-2xl text-[#F4EFE6] mt-2 min-h-[3.5rem] leading-tight">
                {ready ? duetName : <span className="text-white/30 italic text-xl">Select two fragrances…</span>}
              </p>

              {a && b && (combinedMoods.length > 0 || combinedOccasions.length > 0) && (
                <div data-testid="alchemist-traits" className="mt-4 space-y-3.5">
                  {combinedMoods.length > 0 && (
                    <div>
                      <p className="font-mono-label text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">Combined Moods</p>
                      <div className="flex flex-wrap gap-1.5">
                        {combinedMoods.map((m) => (
                          <span key={m} data-testid={`alchemist-mood-${m}`} className="px-2.5 py-1 border border-[#C5A059]/30 bg-[#C5A059]/10 text-[10px] font-mono-label uppercase tracking-wider text-[#C5A059]">{m}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinedOccasions.length > 0 && (
                    <div>
                      <p className="font-mono-label text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">Combined Occasions</p>
                      <div className="flex flex-wrap gap-1.5">
                        {combinedOccasions.map((o) => (
                          <span key={o} data-testid={`alchemist-occasion-${o}`} className="px-2.5 py-1 border border-white/15 text-[10px] font-mono-label uppercase tracking-wider text-white/65">{o}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6">
                <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">Expression</p>
                <div className="flex flex-wrap gap-2">
                  {concs.map((c) => (
                    <button key={c.key} data-testid={`alchemist-conc-${c.key}`} onClick={() => setConc(c)}
                      className={`px-3.5 py-2 border font-mono-label text-[10px] uppercase tracking-wider transition-colors ${conc?.key === c.key ? "border-[#C5A059] bg-[#C5A059]/10 text-[#F4EFE6]" : "border-white/15 text-white/60 hover:border-[#C5A059]/50"}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">Bottle Size</p>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((s) => (
                    <button key={s.key} data-testid={`alchemist-size-${s.key}`} onClick={() => { setSize(s); setQty(0); }}
                      className={`px-4 py-2 border font-mono-label text-[10px] uppercase tracking-wider transition-colors ${size?.key === s.key ? "border-[#C5A059] bg-[#C5A059]/10 text-[#F4EFE6]" : "border-white/15 text-white/60 hover:border-[#C5A059]/50"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                {size && (
                  <div className="mt-3" data-testid="alchemist-moq-note">
                    <p className="text-[11px] text-[#C5A059]/90 font-mono-label uppercase tracking-wider">{pickMsg(msg.duet_moq_template, LEGACY_DUET_TEMPLATE, IMPROVED_DUET_TEMPLATE).replace("{qty}", moq)}</p>
                    <p className="mt-1.5 text-[11px] text-white/55 leading-relaxed">{pickMsg(msg.moq_helper, LEGACY_MOQ_HELPER, IMPROVED_MOQ_HELPER)}</p>
                    <MoqProgress total={inCartForSize} moq={moq} className="mt-3 pt-3 border-t border-white/10" />
                  </div>
                )}
              </div>

              <div className="mt-6 flex items-end justify-between">
                <div>
                  <p data-testid="alchemist-qty-label" className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">Quantity</p>
                  <div className="inline-flex items-center border border-white/15">
                    <button data-testid="alchemist-qty-minus" onClick={() => setQty((x) => Math.max(0, x - 1))} className="w-10 h-10 flex items-center justify-center text-[#F4EFE6] hover:text-[#C5A059]"><Minus className="w-4 h-4" /></button>
                    <span data-testid="alchemist-qty" className="w-10 text-center font-mono-label text-sm text-[#F4EFE6]">{qty}</span>
                    <button data-testid="alchemist-qty-plus" onClick={() => setQty((x) => x + 1)} className="w-10 h-10 flex items-center justify-center text-[#F4EFE6] hover:text-[#C5A059]"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-white/50">Total</p>
                  <p data-testid="alchemist-total" className="font-serif-display text-3xl text-[#C5A059] mt-1">{fmtPrice(total)}</p>
                </div>
              </div>

              <button data-testid="alchemist-add" onClick={add} disabled={!ready || qty < 1} className="btn-gold w-full mt-7 disabled:opacity-40 disabled:cursor-not-allowed">
                <Sparkles className="w-4 h-4" /> Add Duet to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScentAlchemist;
